import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation } from "graphql-hooks";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";

import { CREATE_REPORT_MUTATION } from "../graphQL/report_qry";

const issueOptions = [
  "Inappropriate Image",
  "Inappropriate Title or Description",
  "Others",
];

const FilterSelect = styled(Select)(({ theme }) => ({
  height: "56px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C9C9C9",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C9C9C9",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C9C9C9",
  },
}));

function ClosableSelect({ children, ...props }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  return (
    <FilterSelect
      ref={ref}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      MenuProps={{
        disableScrollLock: true,
        PaperProps: {
          ref: dropdownRef,
        },
      }}
      {...props}
    >
      {children}
    </FilterSelect>
  );
}

export default function ReportDialog({
  open,
  onClose,
  contentType,
  objectId,
  objectUuid,
}) {
  const isMobile = useMediaQuery("(max-width:833px)");
  const [issueOption, setIssueOption] = useState("");
  const [note, setNote] = useState("");

  const [createReport] = useMutation(CREATE_REPORT_MUTATION, {
    awaitRefetchQueries: true,
  });

  const handleSubmit = async() => {
    const { data, error } = await createReport({
        variables: {
          issue: issueOption,
          note: note,
          objectId: parseInt(objectId),
          objectUuid: objectUuid,
          contentType: contentType,
        },
      });

      if (error) {
        console.log("Error", error, data);
      } else {
        console.log("Success");
      }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          width: isMobile ? "361px" : "446px",
          height: isMobile ? "566px" : "492px",
          overflow: "hidden",
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 10,
          top: 10,
          color: "#666",
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.04)",
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Dialog Content */}
      <DialogContent
        sx={{
          textAlign: "center",
          pt: 7,
          pb: 0,
          overflow: "hidden",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          Report
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#999",
            pt: 0.5,
          }}
        >
          The reason you are reporting this item.
        </Typography>

        <Box sx={{ py: "24px" }}>
          <Typography
            variant="body2"
            sx={{
              color: "#999",
              textAlign: "left",
            }}
          >
            Content Type: {contentType}
          </Typography>

          <FormControl
            fullWidth
            size="small"
            sx={{ my: "16px" }}
          >
            <InputLabel
              shrink={true}
              sx={{
                color: "#707070",
                "&.Mui-focused": { color: "#707070" },
              }}
            >
              Issue
            </InputLabel>
            <ClosableSelect
              label="Issue"
              value={issueOption}
              onChange={(e) => {
                setIssueOption(e.target.value)
              }}
              displayEmpty
              sx={{
                color: "#9B9B9B",
                borderRadius: "6px",
                textAlign: "left",
                "&.Mui-focused fieldset": {
                  borderColor: "#ddd !important",
                },
                "&:hover fieldset": {
                  borderColor: "#ddd !important",
                },
              }}
            >
              <MenuItem value="" disabled>
                Select issue
              </MenuItem>
              {issueOptions.map((issue) => (
                <MenuItem
                  key={issue}
                  value={issue}
                >
                  <Typography sx={{ color:"text.primary" }}>
                    {issue}
                  </Typography>
                </MenuItem>
              ))}
            </ClosableSelect>
          </FormControl>

          <TextField
            label="Notes"
            size="small"
            multiline
            minRows={isMobile ? 10 : 6}
            maxRows={isMobile ? 10 : 6}
            value={note}
            fullWidth
            onChange={(e) => setNote(e.target.value)}
            InputProps={{
              sx: {
                "& textarea": {
                  maxHeight: isMobile ? "196px" : "152px",
                  overflowY: "auto",
                },
              },
            }}
            InputLabelProps={{
              shrink: true,
              sx: {
                color: "#707070",
                "&.Mui-focused": { color: "#707070" },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#C9C9C9",
                },
                "&:hover fieldset": {
                  borderColor: "#C9C9C9",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#C9C9C9",
                },
              },
            }}
          />
        </Box>
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions
        sx={{
          justifyContent: "center",
          gap: 0.5,
          pb: 5,
          px: 3,
          pt: 2,
        }}
      >
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            backgroundColor: "#EF5658",
            color: "white",
            borderRadius: "4px",
            px: 3,
            py: 1.5,
            height: "36px",
            fontSize: "14px",
            fontWeight: "meduim",
            textTransform: "uppercase",
            minWidth: "92px",
            "&:hover": {
              backgroundColor: "#D33338",
            },
          }}
        >
          SUBMIT
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#666",
            color: "#666",
            borderRadius: "4px",
            px: 3,
            py: 1.5,
            fontSize: "14px",
            fontWeight: "meduim",
            height: "36px",
            textTransform: "uppercase",
            minWidth: "92px",
            "&:hover": {
              borderColor: "#666",
              backgroundColor: "rgba(0,0,0,0.04)",
            },
          }}
        >
          CANCEL
        </Button>
      </DialogActions>
    </Dialog>
  );
}
